import { useCallback, useMemo } from "react";
import {
  Activitystream,
  useActivitystreams,
} from "../../../../api/activitystreams";
import Async from "react-select/async";
import { filter, get, groupBy, map, orderBy, toLower } from "lodash";
import Label from "../../../shared/form/Label";
import { Control, Controller, useFormContext } from "react-hook-form";
import classNames from "classnames";
import Hint from "../../../shared/form/fields/Hint.tsx";
import { fetchDirectory, MemberEntry } from "../../../directory/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faUser } from "@fortawesome/pro-regular-svg-icons";

export type TargetOption = {
  label: string;
  value: string;
  type?: "stream" | "member";
  images?: MemberEntry["images"];
};

type OptionGroup = {
  label: string;
  options: TargetOption[];
};

const MAX_SELECTIONS = 25;

type RecommendationTargetSelectProps = {
  control?: Control<any>;
  filterCan?: string;
  hint?: string;
};

export default function RecommendationTargetSelect({
  control,
  filterCan = "email_recommend_activity_internally",
  hint,
}: RecommendationTargetSelectProps) {
  const {
    formState: { errors },
    watch,
  } = useFormContext();
  const { data: activitystreams, isLoading } = useActivitystreams();

  const selectedValues = watch("activitystreams");

  const groupedStreams: OptionGroup[] = useMemo(() => {
    const writeableStreams: Activitystream[] = filter(
      activitystreams,
      (stream: Activitystream) =>
        !!get(stream, ["can", filterCan]) && !stream.archived,
    );
    return map(
      groupBy(
        writeableStreams,
        (stream) =>
          stream.category_name ||
          I18n.t(
            "js.activitystreams.composer.stream_targeting.selection.network_streams",
          ),
      ),
      (options, key) => {
        const serializedOptions: TargetOption[] = map(options, (opt) => ({
          label: opt.name,
          value: opt.slug,
          type: "stream" as const,
        }));
        return {
          label: key,
          options: orderBy(
            serializedOptions,
            (stream) => toLower(stream.label),
            "asc",
          ),
        };
      },
    );
  }, [activitystreams, filterCan]);

  const loadOptions = useCallback(
    async (inputValue: string): Promise<OptionGroup[]> => {
      if (!inputValue || inputValue.length < 1) return groupedStreams;

      const filteredStreamGroups = groupedStreams
        .map((group) => ({
          ...group,
          options: group.options.filter((opt) =>
            toLower(opt.label).includes(toLower(inputValue)),
          ),
        }))
        .filter((group) => group.options.length > 0);

      let memberOptions: TargetOption[] = [];
      try {
        const { entries } = await fetchDirectory(
          { q: inputValue, only_members_of_my_admin_groups: true },
          "/directory/members",
        );
        memberOptions = entries
          .filter((e): e is MemberEntry => e.type === "member")
          .map((member) => ({
            type: "member" as const,
            label: member.name,
            value: member.id,
            images: member.images,
          }));
      } catch (e) {
        console.error(e);
      }

      const groups: OptionGroup[] = [...filteredStreamGroups];
      if (memberOptions.length > 0) {
        groups.push({
          label: I18n.t("js.permission_picker.members.group_label"),
          options: memberOptions,
        });
      }
      return groups;
    },
    [groupedStreams],
  );

  const formatOptionLabel = useCallback(
    (option: TargetOption, { context }: { context: "menu" | "value" }) => {
      if (context === "value") {
        return (
          <div className="flex gap-1 items-center">
            {option.type === "member" ? (
              option.images ? (
                <img
                  className="h-4 w-4 rounded-full"
                  src={option.images.small}
                  alt=""
                />
              ) : (
                <FontAwesomeIcon icon={faUser} size="xs" className="fa-fw" />
              )
            ) : (
              <FontAwesomeIcon icon={faUsers} size="xs" className="fa-fw" />
            )}
            {option.label}
          </div>
        );
      }
      return (
        <div className="flex gap-2 items-center">
          {option.type === "member" ? (
            option.images ? (
              <img
                className="h-6 w-6 rounded-full"
                src={option.images.small}
                alt=""
              />
            ) : (
              <FontAwesomeIcon icon={faUser} className="fa-fw" />
            )
          ) : (
            <FontAwesomeIcon icon={faUsers} className="fa-fw" />
          )}
          {option.label}
        </div>
      );
    },
    [],
  );

  return (
    <div className="mb-2">
      <Label label={I18n.t("composer.editor.stream_targeting.label")} />
      <Controller
        name="activitystreams"
        control={control}
        rules={{ required: I18n.t("js.activity.validation.select_stream") }}
        render={({ field }) => (
          <Async<TargetOption, true, OptionGroup>
            loadOptions={loadOptions}
            defaultOptions={groupedStreams}
            formatOptionLabel={formatOptionLabel}
            placeholder={I18n.t("composer.editor.stream_targeting.label")}
            noOptionsMessage={({ inputValue }) =>
              !inputValue
                ? I18n.t("js.plugins.select2.enter_more_characters.one")
                : I18n.t("js.plugins.select2.no_match")
            }
            getOptionValue={(opt) => `${opt.type || "stream"}/${opt.value}`}
            className={classNames("form-select-container", {
              "field-error field-error-live": errors.activitystreams,
            })}
            classNamePrefix="form-select"
            closeMenuOnSelect
            unstyled
            isMulti
            isSearchable
            isLoading={isLoading}
            isOptionDisabled={() =>
              (selectedValues?.length || 0) >= MAX_SELECTIONS
            }
            {...field}
          />
        )}
      />
      {hint && <Hint text={hint} />}
      {selectedValues?.length >= MAX_SELECTIONS && (
        <Hint
          text={I18n.t(
            "js.activitystreams.email_recommendations.max_recipients_hint",
            { count: MAX_SELECTIONS },
          )}
        />
      )}
    </div>
  );
}
